/**
 * src/components/spatial/SpatialObjectSimulator.tsx
 *
 * Stand next to the building without going there.
 *
 * Whether an object speaks depends on where the listener is, what the
 * jurisdiction allows, whether a rule holds, and half a dozen settings
 * spread across three configuration sections. When any one of those is
 * wrong the symptom is identical: nothing happens. This panel exists to
 * turn that silence into a named reason.
 *
 * Two halves. The report is read-only and re-runs on every change to
 * the simulated position, so an operator can drag the participant in
 * and out of range and watch eligibility flip. The rehearsal buttons
 * perform real actions under mock adapters — genuine authorization,
 * genuine rules, genuine queue, nothing published and no audio
 * synthesised — so a rehearsal against a production object cannot be
 * overheard by a passer-by.
 *
 * Nothing here can grant anything. A refusal is shown as a refusal; the
 * value is finding out that the country policy is unset before the
 * event rather than during it.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  inspectSpatialObject,
  previewSpatialTts,
  runSpatialSimulation,
  endSpatialOccasion,
  getSpatialSurface,
  runSpatialOccasion,
  type SpatialInteractionSession,
  type SpatialSimulationAction,
  type SpatialSimulationReport,
} from '../../services/spatial/spatialPresenceClient';
import { blockerText, failureText } from './spatialBlockerText';
import { objectUrlForAudio } from '../../utils/audioObjectUrl';
import SpatialChatPanel from './SpatialChatPanel';

export interface SpatialObjectSimulatorProps {
  objectId: string;
  objectName: string | null;
  walletAddress: string | null;
  canManage: boolean;
  /** Object centroid, used as the starting point for the participant. */
  anchor: { lat: number; lng: number } | null;
}

type ParticipantType = 'human' | 'ai_agent';
type ForceRule = '' | 'pass' | 'fail';

const Verdict: React.FC<{ label: string; ok: boolean | null; detail?: string | null }> = ({
  label, ok, detail,
}) => (
  <div className="spatial-admin-row" style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
    <span aria-hidden style={{ width: 14 }}>
      {ok === null ? '·' : ok ? '✓' : '✕'}
    </span>
    <span style={{ minWidth: 130 }}>{label}</span>
    <span className="spatial-admin-helper" style={{ margin: 0, color: ok === false ? '#b91c1c' : undefined }}>
      {detail || (ok === null ? 'not applicable' : ok ? 'ok' : 'blocked')}
    </span>
  </div>
);

export const SpatialObjectSimulator: React.FC<SpatialObjectSimulatorProps> = ({
  objectId,
  objectName,
  walletAddress,
  canManage,
  anchor,
}) => {
  const [participantType, setParticipantType] = useState<ParticipantType>('human');
  const [lat, setLat] = useState<string>(anchor ? String(anchor.lat) : '');
  const [lng, setLng] = useState<string>(anchor ? String(anchor.lng) : '');
  const [altitude, setAltitude] = useState<string>('');
  const [accuracy, setAccuracy] = useState<string>('');
  const [forceRule, setForceRule] = useState<ForceRule>('');
  const [useWallet, setUseWallet] = useState(true);

  const [report, setReport] = useState<SpatialSimulationReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [speech, setSpeech] = useState<string>('');
  const [chat, setChat] = useState<SpatialInteractionSession | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  useEffect(() => () => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
  }, []);

  const appendLog = useCallback((line: string) => {
    setLog((prev) => [`${new Date().toLocaleTimeString()} · ${line}`, ...prev].slice(0, 30));
  }, []);

  const refresh = useCallback(async () => {
    if (!objectId) return;
    setBusy(true);
    setError(null);
    const result = await inspectSpatialObject(objectId, {
      participantType,
      wallet: useWallet ? walletAddress : null,
      lat: lat ? Number(lat) : null,
      lng: lng ? Number(lng) : null,
      altitudeMeters: altitude ? Number(altitude) : null,
      accuracyMeters: accuracy ? Number(accuracy) : null,
      forceRule: forceRule || null,
    });
    setBusy(false);
    if (!result.ok) {
      setError(failureText(result));
      return;
    }
    setReport(result as unknown as SpatialSimulationReport);
  }, [objectId, participantType, useWallet, walletAddress, lat, lng, altitude, accuracy, forceRule]);

  useEffect(() => { void refresh(); }, [refresh]);

  const act = useCallback(async (action: SpatialSimulationAction, extra: Record<string, unknown> = {}) => {
    setBusy(true);
    setError(null);
    const result = await runSpatialSimulation(objectId, {
      action,
      wallet: useWallet ? walletAddress : null,
      participantType,
      lat: lat ? Number(lat) : null,
      lng: lng ? Number(lng) : null,
      ...extra,
    });
    setBusy(false);

    if (!result.ok) {
      appendLog(`${action} refused — ${blockerText(result.reason)}`);
      setError(failureText(result));
      void refresh();
      return;
    }

    // Opening a conversation returns a session, not an utterance: the
    // greeting lives on `session.greeting`, and reading `text` here is
    // what made a working chat look like a dead button.
    const session = (result as { session?: SpatialInteractionSession }).session;
    if (session?.sessionId) {
      setChat(session);
      appendLog(
        session.greeting
          ? `chat opened: “${session.greeting}”`
          : 'chat opened (the object offered no greeting)',
      );
      void refresh();
      return;
    }

    const spoken = (result as { text?: string }).text;
    appendLog(spoken ? `${action}: “${spoken}”` : `${action} ok`);
    if (spoken) setSpeech(spoken);
    void refresh();
  }, [objectId, useWallet, walletAddress, participantType, lat, lng, appendLog, refresh]);

  /**
   * Rehearse the whole occasion.
   *
   * Facade, broadcast and speech together, which is what an event
   * actually is — and the only way to find out that two of the three are
   * configured and the third is not. Partial success is reported as
   * partial rather than as failure, because two thirds of a National Day
   * still needs the third named, not the whole thing called broken.
   *
   * The surface step defaults to whatever the operator approved, so the
   * flag switch and this button need no shared configuration.
   */
  const runOccasion = useCallback(async () => {
    setBusy(true);
    setError(null);

    const surface = await getSpatialSurface(objectId);
    const contentRef = surface.ok ? (surface.config?.defaultContentRef ?? null) : null;

    const result = await runSpatialOccasion(objectId, {
      wallet: useWallet ? walletAddress : null,
      trigger: 'event',
      reason: 'operator_rehearsal',
      surface: contentRef ? { contentRef } : null,
      broadcast: { title: objectName ? `${objectName} — live` : null },
      speech: { text: speech.trim() || null, intent: 'occasion_open' },
    });
    setBusy(false);

    if (!result.ok) {
      appendLog(`occasion refused — ${blockerText(result.reason)}`);
      setError(failureText(result));
    } else if (result.partial) {
      appendLog(`occasion partial — ran ${result.succeeded.join(', ') || 'nothing'}`);
      setError(
        `Ran ${result.succeeded.join(', ') || 'nothing'}. `
        + result.failed
          .map((step) => `${step}: ${blockerText(result.steps[step]?.reason || 'refused')}`)
          .join(' · '),
      );
    } else {
      appendLog(`occasion ok — ${result.succeeded.join(', ')}`);
    }
    void refresh();
  }, [objectId, objectName, useWallet, walletAddress, speech, appendLog, refresh]);

  const stopOccasion = useCallback(async () => {
    setBusy(true);
    setError(null);
    const result = await endSpatialOccasion(objectId, 'operator_rehearsal_end');
    setBusy(false);
    appendLog(result.ok ? 'occasion ended' : `occasion end refused — ${blockerText(result.reason)}`);
    void refresh();
  }, [objectId, appendLog, refresh]);

  /**
   * Hear the line locally.
   *
   * The one call that reaches Azure, because whether a voice sounds
   * right is not a question a mock can answer. Playback is triggered by
   * this click, which is also what satisfies the browser's autoplay
   * policy — no gesture, no audio, and we do not try to route around
   * that.
   */
  const playTts = useCallback(async () => {
    const text = speech.trim();
    if (!text) return;
    setBusy(true);
    const result = await previewSpatialTts(objectId, text);
    setBusy(false);

    if (!result.ok) {
      appendLog(`tts refused — ${blockerText(result.reason)}`);
      setError(failureText(result));
      return;
    }
    if (!result.audioBase64) {
      appendLog('tts returned no audio (mock or synthesiser unavailable)');
      return;
    }
    // An object URL rather than a `data:` URL, matching what the live
    // player does with caption audio. Both would need widening the CSP,
    // and `blob:` is the narrower of the two.
    const url = objectUrlForAudio(result.audioBase64, result.mime);
    if (!url) {
      appendLog('tts audio could not be decoded');
      return;
    }
    if (!audioRef.current) audioRef.current = new Audio();
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    audioUrlRef.current = url;
    audioRef.current.src = url;
    audioRef.current.playbackRate = result.playbackRate || 1;
    void audioRef.current.play().catch(() => {
      appendLog('the browser refused playback; click again to grant audio');
    });
    appendLog(`tts ok · ${result.voice || 'default voice'} · ${result.durationMs ?? '?'} ms`);
  }, [objectId, speech, appendLog]);

  if (!objectId) {
    return (
      <div className="spatial-admin-section">
        <h3>Test</h3>
        <div className="spatial-admin-helper">
          Save the object first — there is nothing to simulate until it exists.
        </div>
      </div>
    );
  }

  const p = report?.proximity;

  return (
    <div className="spatial-admin-section">
      <h3>Test &amp; preview</h3>
      <div className="spatial-admin-helper">
        Rehearsals run against {objectName || 'this object'} with mock
        transport and mock speech: the authority, policy and rule checks
        are the real ones, but nothing is published and nobody nearby
        hears anything.
      </div>

      {/* ── Participant ─────────────────────────────────────────── */}
      <h4 style={{ margin: '10px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>
        Simulated participant
      </h4>

      <div className="spatial-admin-row">
        <label>Type</label>
        <select
          className="spatial-admin-select"
          value={participantType}
          onChange={(e) => setParticipantType(e.target.value as ParticipantType)}
        >
          <option value="human">Human</option>
          <option value="ai_agent">AI agent</option>
        </select>
      </div>

      <div className="spatial-admin-row">
        <label>Latitude</label>
        <input className="spatial-admin-input" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="25.197197" />
      </div>
      <div className="spatial-admin-row">
        <label>Longitude</label>
        <input className="spatial-admin-input" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.274376" />
      </div>
      <div className="spatial-admin-row">
        <label>Altitude (m)</label>
        <input className="spatial-admin-input" value={altitude} onChange={(e) => setAltitude(e.target.value)} placeholder="(unknown)" />
        <div className="spatial-admin-helper">
          Leave blank to simulate a fix with no usable altitude, which is the common case.
        </div>
      </div>
      <div className="spatial-admin-row">
        <label>Accuracy (m)</label>
        <input className="spatial-admin-input" value={accuracy} onChange={(e) => setAccuracy(e.target.value)} placeholder="e.g. 30" />
      </div>

      <div className="spatial-admin-quickbar">
        <button
          className="spatial-admin-button"
          disabled={!anchor}
          onClick={() => { if (anchor) { setLat(String(anchor.lat)); setLng(String(anchor.lng)); } }}
        >
          At the object
        </button>
        <button
          className="spatial-admin-button"
          disabled={!anchor || !p?.playbackRadiusMeters}
          onClick={() => {
            if (!anchor || !p?.playbackRadiusMeters) return;
            // Half a radius due north: comfortably inside, and visibly
            // not the centroid, so distance-aware volume is exercised.
            const metresPerDegree = 111_320;
            setLat(String(anchor.lat + (p.playbackRadiusMeters / 2) / metresPerDegree));
            setLng(String(anchor.lng));
          }}
        >
          Inside radius
        </button>
        <button
          className="spatial-admin-button"
          disabled={!anchor || !p?.exitRadiusMeters}
          onClick={() => {
            if (!anchor || !p?.exitRadiusMeters) return;
            const metresPerDegree = 111_320;
            setLat(String(anchor.lat + (p.exitRadiusMeters * 2) / metresPerDegree));
            setLng(String(anchor.lng));
          }}
        >
          Outside radius
        </button>
      </div>

      <div className="spatial-admin-row">
        <label>Execution rule</label>
        <select
          className="spatial-admin-select"
          value={forceRule}
          onChange={(e) => setForceRule(e.target.value as ForceRule)}
        >
          <option value="">Evaluate for real</option>
          <option value="pass">Force pass</option>
          <option value="fail">Force fail</option>
        </select>
        <div className="spatial-admin-helper">
          A forced verdict only affects this report, and is labelled as forced.
          Rehearsed actions still evaluate the rule properly.
        </div>
      </div>

      <div className="spatial-admin-row">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} />
          <span>Act as the connected wallet</span>
        </label>
        <div className="spatial-admin-helper">
          Unchecked, the report falls back to the recorded standing authority —
          which is what an autonomous trigger would actually spend.
        </div>
      </div>

      {error && <div className="spatial-admin-helper" style={{ color: '#b91c1c' }}>{error}</div>}

      {/* ── Report ──────────────────────────────────────────────── */}
      {report && (
        <>
          <h4 style={{ margin: '12px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>
            Verdict
          </h4>

          <div
            className="spatial-admin-helper"
            style={{ color: report.eligible ? '#047857' : '#b45309', fontWeight: 600 }}
          >
            {report.eligible
              ? 'A listener standing here would be offered the player.'
              : 'Nothing would be offered here.'}
          </div>

          {report.blockers.length > 0 && (
            <ul className="spatial-admin-helper" style={{ margin: '4px 0 8px 16px' }}>
              {report.blockers.map((b) => <li key={b}>{blockerText(b)}</li>)}
            </ul>
          )}

          <Verdict
            label="Distance"
            ok={p?.insideEnterRadius ?? null}
            detail={p?.distanceMeters != null
              ? `${Math.round(p.distanceMeters)} m from the ${p.anchor?.source || 'anchor'}`
              + (p.playbackRadiusMeters ? ` · radius ${p.playbackRadiusMeters} m` : '')
              + (p.volume != null ? ` · volume ${p.volume.toFixed(2)}` : '')
              : 'no position given'}
          />
          <Verdict
            label="Jurisdiction"
            ok={report.jurisdiction.ok === true}
            detail={report.jurisdiction.reason
              ? String(report.jurisdiction.reason)
              : `${report.object.countryCode || '—'} · allowed`}
          />
          <Verdict
            label="Compliance"
            ok={report.compliance.ok === true}
            detail={report.compliance.reason ? String(report.compliance.reason) : 'not listed'}
          />
          <Verdict
            label="Authority"
            ok={report.authority.ok === true}
            detail={report.authority.reason
              ? String(report.authority.reason)
              : `${(report.authority.wallet || '').slice(0, 8)}… may manage`}
          />
          <Verdict
            label="Execution rule"
            ok={report.rule ? report.rule.passing === true : null}
            detail={report.rule
              ? `${report.rule.forced ? 'forced · ' : ''}${report.rule.reason || 'passing'}`
              : 'no rule configured'}
          />
          <Verdict
            label="Audience"
            ok={report.audience.allowed}
            detail={`${report.audience.participantType}${report.audience.requiresWallet ? ' · wallet required' : ''}`}
          />
          <Verdict
            label="Broadcast"
            ok={report.session ? report.session.status === 'LIVE' : null}
            detail={report.session
              ? `${report.session.status} · ${report.session.viewerCount} listening · ${report.session.transport}`
              : 'not broadcasting'}
          />
          {report.placement?.altitude && (
            <Verdict
              label="Floor"
              ok={report.placement.altitude.eligible}
              detail={`${report.placement.altitude.confidence}`
                + (report.placement.floors?.length
                  ? ` · ${report.placement.floors.map((f) => f.name || f.floorNumber).join(', ')}`
                  : '')}
            />
          )}
          {report.surface.activation != null && (
            <Verdict
              label="Surface"
              ok
              detail={`showing ${String((report.surface.activation as { contentRef?: string }).contentRef)}`}
            />
          )}
        </>
      )}

      {/* ── Actions ─────────────────────────────────────────────── */}
      <h4 style={{ margin: '12px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>
        Rehearse
      </h4>

      {!canManage && (
        <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
          This wallet may not manage the object, so every rehearsal will be refused —
          which is itself a useful thing to confirm.
        </div>
      )}

      <div className="spatial-admin-quickbar">
        <button className="spatial-admin-button" disabled={busy} onClick={() => void act('start_broadcast')}>
          Start broadcast
        </button>
        <button className="spatial-admin-button" disabled={busy} onClick={() => void act('stop_broadcast')}>
          Stop broadcast
        </button>
        <button className="spatial-admin-button" disabled={busy} onClick={() => void act('speak')}>
          Generate AI response
        </button>
        <button className="spatial-admin-button" disabled={busy} onClick={() => void act('interact')}>
          Open chat
        </button>
        <button className="spatial-admin-button" disabled={busy} onClick={() => void refresh()}>
          ↻ Re-check
        </button>
      </div>

      <div className="spatial-admin-quickbar">
        <button className="spatial-admin-button" disabled={busy} onClick={() => void runOccasion()}>
          🎉 Run occasion
        </button>
        <button className="spatial-admin-button" disabled={busy} onClick={() => void stopOccasion()}>
          End occasion
        </button>
      </div>
      <div className="spatial-admin-helper">
        One occasion is the facade, the broadcast and the opening line
        together — the same sequence a scheduled event runs, so a rehearsal
        here is the rehearsal that matters. Unlike the buttons above, this
        one is not mocked: it lights the real facade and starts a real
        broadcast.
      </div>

      {chat && (
        <SpatialChatPanel
          key={chat.sessionId}
          session={chat}
          objectName={objectName}
          // An agent participant reads the reply; only a person needs it
          // synthesised.
          speakReplies={participantType === 'human'}
          onLog={appendLog}
          onClosed={() => void refresh()}
        />
      )}

      <div className="spatial-admin-row">
        <label>Line to speak</label>
        <textarea
          className="spatial-admin-input"
          rows={2}
          value={speech}
          placeholder="Leave blank and press Generate AI response."
          onChange={(e) => setSpeech(e.target.value)}
        />
        <div className="spatial-admin-quickbar">
          <button className="spatial-admin-button" disabled={busy || !speech.trim()} onClick={() => void playTts()}>
            🔊 Hear it locally
          </button>
        </div>
        <div className="spatial-admin-helper">
          Synthesised and played in this tab only. Nobody near the object hears it.
        </div>
      </div>

      {/* ── Emitted events ──────────────────────────────────────── */}
      {log.length > 0 && (
        <>
          <h4 style={{ margin: '12px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>
            Session log
          </h4>
          <div className="spatial-admin-helper" style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {log.map((line, i) => <div key={i}>{line}</div>)}
          </div>
        </>
      )}

      {report && report.events.length > 0 && (
        <>
          <h4 style={{ margin: '12px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>
            Emitted events
          </h4>
          <div className="spatial-admin-helper" style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {report.events.slice(-12).reverse().map((e, i) => (
              <div key={`${e.at}-${i}`}>{e.at.slice(11, 19)} · {e.type}</div>
            ))}
          </div>
        </>
      )}

      {report?.aiContext && (
        <details className="spatial-admin-row">
          <summary style={{ cursor: 'pointer' }}>AI context handed to the model</summary>
          <pre
            className="spatial-admin-helper"
            style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 240, overflow: 'auto' }}
          >
            {JSON.stringify(report.aiContext, null, 2)}
          </pre>
          <div className="spatial-admin-helper">
            Allow-listed: object metadata is attacker-influenced in general,
            so anything not on the list never reaches the model.
          </div>
        </details>
      )}
    </div>
  );
};

export default SpatialObjectSimulator;
